import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { debounce } from "lodash";

import { useWorkflowById } from "../../../../utils/reactQuery/workflows";
import Kanban from ".";
import Loader from "../../../../components/Loader";
import { useCards } from "../../../../utils/reactQuery/cards";
import { useResponsive } from "../../../../hooks/useResponsive";
import MobileKanban from "./mobileKanban";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import useBoardIdByKey from "../../../../hooks/useBoardIdByKey";

function Workflows({ workflowId, boardName }) {
  const { id } = useBoardIdByKey();
  const { data } = useWorkflowById(workflowId, id);
  const isMobile = useResponsive("down", "md");
  const { cards } = useCards(id);
  const [filteredCards, setFilteredCards] = useState([]);
  const [originalCardsData, setOriginalCardsData] = useState(null);
  const [sameWorkFlowIdcards, setSameWorkFlowIdcards] = useState([]);
  const [searchParams] = useSearchParams();
  const [estimated, setEstimated] = useCustomSearchParams("estimated");

  // Memoize search params to prevent unnecessary re-renders
  const searchParamsData = useMemo(() => ({
    filterUser: searchParams.get("search")?.split(',') || [],
    noMileStoneSearch: searchParams.get("noMilestone") === "true",
    cardId: searchParams.get("card_id")
  }), [searchParams]);

  // Redux state
  const searchText = useSelector((state) => state.search.searchText);
  const epicSearch = useSelector((state) => state.epic.searchEpic);

  // Initialize cards when they first load
  useEffect(() => {
    if (cards?.data?.data?.responseData) {
      setOriginalCardsData(cards.data.data.responseData);
      setFilteredCards(cards.data.data.responseData);
    }
  }, [cards]);

  // Memoize the filter functions
  const filterFunctions = useMemo(() => ({
    searchText: (cards) => {
      if (!searchText) return cards;
      return cards?.filter(
        (cardData) =>
          (cardData?.card?.title?.toLowerCase().includes(searchText.toLowerCase())) ||
          (cardData?.card?.assigned_to_user?.first_name?.toLowerCase().includes(searchText.toLowerCase())) ||
          (cardData?.card?.assigned_to_user?.last_name?.toLowerCase().includes(searchText.toLowerCase())) ||
          (cardData?.key?.toLowerCase().includes(searchText.toLowerCase())) ||
          (cardData?.card?.card_id?.toString().toLowerCase().includes(searchText.toLowerCase())) ||
          (cardData?.card?.epic?.epic_name?.toLowerCase().includes(searchText.toLowerCase())) ||
          (cardData?.labels?.some((ele) => ele?.toLowerCase().includes(searchText.toLowerCase())))
      );
    },
    user: (cards) => {
      if (!searchParamsData.filterUser.length) return cards;
      return cards?.filter((el) => 
        searchParamsData.filterUser.includes(String(el?.card?.assigned_to_user_id))
      );
    },
    epic: (cards) => {
      if (!epicSearch?.length) return cards;
      return cards?.filter((item) =>
        epicSearch.includes(item?.card?.epic_id)
      );
    },
    milestone: (cards) => {
      if (!searchParamsData.noMileStoneSearch) return cards;
      return cards?.filter((item) => item?.card?.epic_id == null);
    },
    estimated: (cards) => {
      if (estimated === "All" || !estimated) return cards;
      if (estimated === "Watching") return cards.filter((card) => card?.card?.is_watch_list);
      if (estimated === "Estimated") return cards.filter((card) => card.card.allocated_hours);
      if (estimated === "Not Estimated") return cards.filter((card) => !card.card.allocated_hours);
    }
  }), [searchText, searchParamsData, epicSearch, estimated]);

  // Apply all filters to the cards
  const applyAllFilters = useCallback((cardsToFilter) => {
    if (!cardsToFilter?.length) return [];
    
    let result = [...cardsToFilter];
    result = filterFunctions.user(result);
    result = filterFunctions.epic(result);
    result = filterFunctions.milestone(result);
    result = filterFunctions.searchText(result);
    result = filterFunctions.estimated(result);
    
    return result;
  }, [filterFunctions]);

  // Memoize the debounced filter function
  const debouncedFilter = useMemo(() => 
    debounce((originalCards) => {
      const filteredResult = applyAllFilters(originalCards);
      setFilteredCards(filteredResult);
    }, 300),
    [applyAllFilters]
  );

  // Apply filters when dependencies change
  useEffect(() => {
    if (!originalCardsData) return;
    
    debouncedFilter(originalCardsData);
    
    return () => {
      debouncedFilter.cancel();
    };
  }, [originalCardsData, debouncedFilter]);

  // Listen for updates to the cards query data and reapply filters
  useEffect(() => {
    if (!cards?.data?.data?.responseData) return;
    
    // Store the original data
    setOriginalCardsData(cards.data.data.responseData);
    
    // Apply filters to the new data
    const newFilteredCards = applyAllFilters(cards.data.data.responseData);
    setFilteredCards(newFilteredCards);
  }, [cards, applyAllFilters]);

  // Handle same workflow cards for card detail view
  useEffect(() => {
    if (!searchParamsData.cardId || !filteredCards?.length) {
      setSameWorkFlowIdcards([]);
      return;
    }

    const result = filteredCards.find(
      (item) => item?.card?.card_id === Number(searchParamsData.cardId)
    );
    
    if (!result) {
      setSameWorkFlowIdcards([]);
      return;
    }

    const filterCardsByWorkflowStep = filteredCards.filter(
      (cardData) => result?.card?.workflow_step_id === cardData?.card?.workflow_step_id
    );
    setSameWorkFlowIdcards(filterCardsByWorkflowStep);
  }, [searchParamsData.cardId, filteredCards]);

  // Memoize the sort function
  // const sortCards = useCallback((cards) => {
  //   if (!cards?.length) return [];
  //   return [...cards].sort((a, b) => {
  //     if (a.due_date && b.due_date) {
  //       return new Date(a.due_date) - new Date(b.due_date);
  //     }
  //     if (a.due_date) return -1;
  //     if (b.due_date) return 1;
  //     return new Date(a.date_created) - new Date(b.date_created);
  //   });
  // }, []);

  // Memoize sorted cards
  // const sortedFilteredCards = useMemo(() => 
  //   sortCards(filteredCards),
  //   [filteredCards, sortCards]
  // );

  if (!data?.data?.data?.responseData) {
    return (
      <div className="loader-center">
        <Loader color={"black"} />
      </div>
    );
  }

  return (
    <>
      {isMobile ? (
        <Box>
          <MobileKanban
            workflow={data.data.data.responseData}
            data={filteredCards}
            cards={filteredCards}
            createCard={true}
          />
        </Box>
      ) : (
        <Kanban
          boardName={boardName}
          id={id}
          workflowSteps={data.data.data.responseData.sort(
            (a, b) => a.step_sequence - b.step_sequence
          )}
          cards={filteredCards}
          sameWorkFlowIdcards={sameWorkFlowIdcards}
        />
      )}
    </>
  );
}

export default React.memo(Workflows);
